import { useState, useRef, useEffect, useMemo } from 'react';
import styles from './styles.module.css';
import Popup from 'ui/Popup';
import Spinner from 'ui/Spiner';
import { getDeviceById, getDeviceModelById } from 'services/device.services';
import { address_server_short } from 'config';
import { PUBLIC_ASSETS } from 'config';
import { observer } from 'mobx-react-lite';
import { projectsStore } from 'store/mobx/projects.store';
import { formatDate } from 'helpers/utils';

export const DeviceWrapper = observer(({ children, mode = 'model', id }) => {
  const { selectedProject } = projectsStore;
  const [device, setDevice] = useState(null);
  const [loaded, setIsLoaded] = useState(false);
  const popupRef = useRef(null);
  const [showAll, setShowAll] = useState(false);

  const specifications = device?.specifications || [];
  const hasMoreThanFour = specifications?.length > 6;
  const visibleSpecs = showAll ? specifications : specifications?.slice(0, 6);

  useEffect(() => {
    if (!popupRef.current) return;

    const unsubscribe = popupRef.current.subscribe((isOpen) => {
      if (isOpen) {
        loadModel();
      }
    });

    return () => unsubscribe();
  }, [id, mode]);

  const loadModel = async () => {
    if (device) return;
    setIsLoaded(false);

    if (mode === 'model') {
      const res = await getDeviceModelById(id);

      if (res.data) {
        setDevice(res.data);
        setIsLoaded(true);
      }
    } else {
      const deviceId = selectedProject?.devices?.find((device) => device?.device?.id_model == id)
        ?.device?.id;

      if (deviceId) {
        const res = await getDeviceById(deviceId);
        setDevice(res.data);
        setIsLoaded(true);
      } else {
        setIsLoaded(true);
      }
    }
  };

  const portalContent = useMemo(() => {
    if (!loaded) return <Spinner />;

    if (loaded && !device) return <div>Не удалось получить информацию об устройстве</div>;

    if (mode === 'device') {
      return (
        <div className={styles.container}>
          <div className={styles.cardInfo}>
            <div
              style={{
                display: 'flex',
              }}
            >
              <div className={styles.image}>
                <img
                  src={address_server_short + PUBLIC_ASSETS + device.image}
                  alt="N/A"
                  className={styles.picture}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <div className={styles.value}>{device.model}</div>
                <div className={styles.label}>(инв. {device.inventory_number})</div>
              </div>
            </div>
            <div className={styles.description}>
              <div className={styles.label}>Описание</div>
              <div className={styles.value}>{device.description}</div>
            </div>
          </div>

          <div className={styles.cardInfo}>
            <div className={styles.line}>
              <div className={styles.label}>Пользовательские параметры</div>
              <div className={styles.value}>
                {device?.user_fields?.length
                  ? device?.user_fields?.map((field) => field.name).join(', ')
                  : 'Не указано'}
              </div>
            </div>
          </div>

          <div className={styles.cardInfo}>
            <div className={styles.line}>
              <div className={styles.label}>ID</div>
              <div className={styles.value}>{device.id}</div>
            </div>

            <div className={styles.line}>
              <div className={styles.label}>Название</div>
              <div className={styles.value}>{device.name}</div>
            </div>

            <div className={styles.line}>
              <div className={styles.label}>Модель</div>
              <div className={styles.value}>{device.model}</div>
            </div>

            <div className={styles.line}>
              <div className={styles.label}>Дата создания</div>
              <div className={styles.value}>{formatDate(new Date(device.created_at))}</div>
            </div>

            <div className={styles.line}>
              <div className={styles.label}>Дата последнего обновления</div>
              <div className={styles.value}>{formatDate(new Date(device.updated_at))}</div>
            </div>
          </div>
          <div className={styles.cardInfo}>
            <div className={styles.block}>
              <div className={styles.value}>Характеристики</div>
              <div>
                <table className={styles.options}>
                  <tbody>
                    {device.device_model?.specifications?.length ? (
                      device.device_model?.specifications?.map((spec, index) => (
                        <tr key={index}>
                          <td className={styles.cell}>{spec.name}</td>
                          <td className={styles.cell}>{spec.value}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className={styles.cell} colSpan={2}>
                          Не указано
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.cardInfo}>
          <div
            style={{
              display: 'flex',
            }}
          >
            <div className={styles.image}>
              <img
                src={address_server_short + PUBLIC_ASSETS + device.image}
                alt="N/A"
                className={styles.picture}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <div className={styles.value}>{device.name}</div>
            </div>
          </div>
          <div className={styles.description}>
            <div className={styles.label}>Описание</div>
            <div className={styles.value}>{device.description}</div>
          </div>
        </div>

        <div className={styles.cardInfo}>
          <div className={styles.line}>
            <div className={styles.label}>Пользовательские параметры</div>
            <div className={styles.value}>
              {device?.user_fields?.length
                ? device?.user_fields?.map((field) => field.name).join(', ')
                : 'Не указано'}
            </div>
          </div>
        </div>

        <div className={styles.cardInfo}>
          <div className={styles.block}>
            <div className={styles.value}>Характеристики</div>
            <div>
              <table className={styles.options}>
                <tbody>
                  {device.device_model?.specifications?.length ? (
                    device.device_model?.specifications?.map((spec, index) => (
                      <tr key={index}>
                        <td className={styles.cell}>{spec.name}</td>
                        <td className={styles.cell}>{spec.value}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className={styles.cell} colSpan={2}>
                        Не указано
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }, [device, loaded, specifications, hasMoreThanFour, visibleSpecs, showAll]);

  return (
    <Popup ref={popupRef}>
      {children}

      <Popup.Portal>{portalContent}</Popup.Portal>
    </Popup>
  );
});

export default DeviceWrapper;
